import React, { useRef } from 'react';

import { MenuItem } from '@mui/material';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useProgramLibraryStore } from 'store/programLibrary.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { useQueryGetProjectVersions } from 'api/queries/getProjectVersions.query';

import { CustomizedDropdownMenu } from 'components/common/CustomizedDropdownMenu/CustomizedDropdownMenu';
import { CustomizedTooltip } from 'components/common/CustomizedTooltip/CustomizedTooltip';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import {
    isTypeNotMatchVersionsType,
    mppVersions,
    xerAnalyze,
    xlsxAnalyze,
} from 'components/common/FileUploader/fileUploader.utils';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { Details, FileExtensions } from 'components/common/UploadFileModal/components/programLibrary.types';

import { Button } from 'components/DesignSystem/Button';

const FileUploader = () => {
    const { opcEnabled } = useUserHook();
    const { update } = useProgramLibraryStore();
    const { project, contract } = useProjectTreeStore();
    const { data: projectVersions } = useQueryGetProjectVersions({
        projectId: project?.id,
        contractId: contract?.id,
    });
    const { handleSnackBar } = useCustomSnackBar();

    const handleAfterUpload = ({ versions, file, type }: Details) => {
        if (versions.some((item) => item.versionDate === 'Invalid date')) {
            handleSnackBar('Invalid file. Missing Data Date', 'error');
        } else {
            const showWarningModal = isTypeNotMatchVersionsType({ versions: projectVersions, type });
            projectVersions &&
                update({
                    modalShow: !showWarningModal,
                    details: { versions, file, type },
                    showWarningModal: showWarningModal,
                });
        }
    };

    const handleFileReadXer = ({ file, fileReader }: { file: File; fileReader: FileReader }) => {
        const versions = xerAnalyze(fileReader);
        handleAfterUpload({ versions, file, type: FileExtensions.xer });
    };
    const handleFileReadMpp = ({ file }) => {
        handleAfterUpload({ versions: mppVersions, file, type: FileExtensions.mpp });
    };
    const handleFileReadXlsx = ({ file }) => {
        if (contract) {
            const versions = xlsxAnalyze(contract.title);
            handleAfterUpload({
                versions,
                file,
                type: FileExtensions.xlsx,
            });
        }
    };

    const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target?.files) {
            const file = e.target.files[0];
            const extArr = file.name.split('.');
            const extension = extArr[extArr.length - 1].toLowerCase() as FileExtensions;

            if (!Object.values(FileExtensions).includes(extension)) {
                handleSnackBar('Unsupported file type', 'error');
                return;
            }

            const fileReader = new FileReader();
            if (extension === FileExtensions.xer) {
                fileReader.onloadend = () => handleFileReadXer({ file, fileReader });
                fileReader.readAsText(file);
            }

            if (extension === FileExtensions.mpp) {
                fileReader.onloadend = () => handleFileReadMpp({ file });
                fileReader.readAsArrayBuffer(file);
            }

            if (extension === FileExtensions.xlsx) {
                fileReader.onloadend = () => handleFileReadXlsx({ file });
                fileReader.readAsArrayBuffer(file);
            }
            // must clean input so after closing modal user can open it again
            e.target.value = '';
        }
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleOpcModal = () => {
        update({
            showOpcModal: true,
        });
    };

    if (opcEnabled) {
        return (
            <>
                <input
                    type={'file'}
                    id={'file'}
                    accept={'.xer, .mpp, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                    hidden
                    ref={fileInputRef}
                    onChange={(e) => handleFileChosen(e)}
                />
                <CustomizedDropdownMenu
                    triggerElement={
                        <CustomizedTooltip
                            tooltipPosition={TooltipPosition.Top}
                            tooltipContent={`Upload file / OPC import`}
                            triggerElement={
                                <Button circle component={'label'} id={'program-library-upload-file-button'}>
                                    <MuiIcon icon={'add'} color={'var(--button-primary-fg)'} />
                                </Button>
                            }
                        />
                    }
                >
                    <MenuItem onClick={() => fileInputRef.current?.click()}>Upload file</MenuItem>
                    <MenuItem onClick={() => handleOpcModal()}>OPC import</MenuItem>
                </CustomizedDropdownMenu>
            </>
        );
    }

    return (
        <CustomizedTooltip
            tooltipPosition={TooltipPosition.Top}
            tooltipContent={`Upload File`}
            triggerElement={
                <Button circle component={'label'} id={'program-library-upload-file-button'}>
                    <input
                        type={'file'}
                        id={'file'}
                        accept={'.xer, .mpp, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                        hidden
                        onChange={(e) => handleFileChosen(e)}
                    />
                    <MuiIcon icon={'add'} color={'var(--button-primary-fg)'} />
                </Button>
            }
        />
    );
};

export default FileUploader;
